<template>
    <div ref="container"
         class="wb-scroller"
         :class="classes"
    >
        <div ref="content"
             :class="classesContent"
             class="wb-scroller__content"
             @mousedown="handleMouseDown"
             @mousemove="handleMouseMove"
             @mouseup="handleMouseUp"
             @mouseleave="handleMouseLeave"
        >
            <slot v-bind="{ isStart: state.isStart, isEnd: state.isEnd, isScrolling: state.isScrolling }" />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { useWindowStore } from '~/src/shared/pinia/useWindowStore'

    const props = withDefaults(
        defineProps<{
            row?: boolean
            column?: boolean
            wrap?: boolean
            showGradient?: boolean
        }>(),
        {
            wrap: false,
            column: false,
        }
    )

    const state = ref({
        startX: 0,
        isTouched: false,
        isStart: true,
        isEnd: false,
        isScrolling: false,
        maxScroll: 0,
    })

    const classesContent = computed(() => ({
        _row: props.row,
        _column: props.column,
        _wrap: props.wrap,
    }))

    const classes = computed(() => ({
        '_is-start': state.value.isStart,
        '_is-end': state.value.isEnd,
        '_is-scrolling': state.value.isScrolling,
        '_is-gradient': props.showGradient,
    }))

    const container = ref<HTMLElement | null>(null)
    const content = ref<HTMLElement | null>(null)

    const windowStore = useWindowStore()
    const isMobile = computed(() => windowStore.isMobile)

    onMounted(() => {
        if (container.value && content.value) {
            state.value.maxScroll = Math.abs(container.value.offsetWidth - content.value.scrollWidth)
        }
    })

    function handleMouseDown(_event: MouseEvent) {
        if (!isMobile.value) {
            state.value.isTouched = true
            state.value.startX = _event.clientX
        }
    }

    function handleMouseMove(_event: MouseEvent) {
        if (!isMobile.value && state.value.isTouched && content.value) {
            const diff = _event.clientX - state.value.startX
            const scrollLeft = content.value.scrollLeft - diff

            if (scrollLeft < 0) {
                content.value.scrollLeft = 0
                state.value.isStart = true
                state.value.isEnd = false
                return
            } else if (scrollLeft > state.value.maxScroll) {
                content.value.scrollLeft = state.value.maxScroll
                state.value.isStart = false
                state.value.isEnd = true
                return
            } else {
                state.value.isScrolling = true
                content.value.scrollLeft = scrollLeft
                state.value.isStart = content.value.scrollLeft === 0
                state.value.isEnd = content.value.scrollLeft === state.value.maxScroll
            }

            state.value.startX = _event.clientX
        }
    }

    function handleMouseUp(_event: MouseEvent) {
        if (!isMobile.value) {
            state.value.isTouched = false
            state.value.isScrolling = false
        }
    }

    function handleMouseLeave(_event: MouseEvent) {
        if (!isMobile.value) {
            state.value.isTouched = false
            state.value.isScrolling = false
        }
    }
</script>

<style scoped lang="scss">
    .wb-scroller {
        position: relative;

        &__content {
            overflow-x: auto;
            overflow-y: clip;

            @include hide-scrollbar;

            &._row {
                display: flex;
                flex-flow: row;
            }

            &._column {
                display: flex;
                flex-flow: column;
            }

            &._wrap {
                flex-wrap: wrap;
            }
        }

        &._is-gradient {
            &._is-end {
                &:after {
                    display: none;
                }

                &:before {
                    display: block;
                }
            }

            &._is-start {
                &:after {
                    display: block;
                }

                &:before {
                    display: none;
                }
            }

            &:before {
                content: "";
                position: absolute;
                top: 0;
                left: 0;
                z-index: 1;
                display: block;
                width: 20px;
                height: 100%;
                background: linear-gradient(270deg, rgb(255 255 255 / 0%) 30%, #EFF6FF 100%);
            }

            &:after {
                content: "";
                position: absolute;
                top: 0;
                right: 0;
                z-index: 1;
                display: block;
                width: 20px;
                height: 100%;
                background: linear-gradient(90deg, rgb(255 255 255 / 0%) 30%, #EFF6FF 100%);
            }
        }
    }
</style>
